import { Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconFile,
  IconFileAlert,
  IconFileTextShield,
  IconFileTime,
  IconHistory,
  IconLock,
  IconShieldLock,
} from '@tabler/icons-react';
import cn from 'classnames';
import * as React from 'react';

import { getDocumentLockBadgeLabel, getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import type { AvailabilityBlockReason } from '@/entities/Tree';

import s from './DocumentAvailabilityIcon.module.scss';

const ICON_SIZE = 16;
const LOCK_BADGE_SIZE = 8;

type Props = {
  reason: AvailabilityBlockReason | null | undefined;
  className?: string;
  /**
   * `file` — иконки file-* (документы в дереве и поиске).
   * `folder` — обычные пиктограммы рядом с папкой (щит, история, alert, замок).
   */
  variant?: 'file' | 'folder';
};

const DocumentAvailabilityIcon: React.FC<Props> = ({ reason, className, variant = 'file' }) => {
  const ariaLabel = getDocumentLockBadgeLabel(reason);
  const tooltip = getDocumentLockMessage(reason);

  const icon = React.useMemo(() => {
    if (variant === 'folder') {
      if (reason === 'dsb_check') {
        return <IconShieldLock size={ICON_SIZE} color="var(--mantine-color-orange-6)" stroke={2} />;
      }

      if (reason === 'processing') {
        return <IconHistory size={ICON_SIZE} color="var(--mantine-color-blue-6)" stroke={2} />;
      }

      if (reason === 'processing_error') {
        return <IconAlertCircle size={ICON_SIZE} color="var(--mantine-color-red-9)" stroke={2.25} />;
      }

      return <IconLock size={ICON_SIZE} color="var(--mantine-color-gray-6)" stroke={2} />;
    }

    if (reason === 'dsb_check') {
      return <IconFileTextShield size={ICON_SIZE} color="var(--mantine-color-orange-6)" stroke={2} />;
    }

    if (reason === 'processing') {
      return <IconFileTime size={ICON_SIZE} color="var(--mantine-color-blue-6)" stroke={2} />;
    }

    if (reason === 'processing_error') {
      return <IconFileAlert size={ICON_SIZE} color="var(--mantine-color-red-9)" stroke={2.25} />;
    }

    return (
      <span className={s.root__file}>
        <IconFile size={ICON_SIZE} color="var(--mantine-color-gray-6)" stroke={2} />
        <IconLock
          size={LOCK_BADGE_SIZE}
          className={s['root__file-lock']}
          color="var(--mantine-color-gray-6)"
          stroke={2}
        />
      </span>
    );
  }, [reason, variant]);

  const body = (
    <span className={cn(s.root, className)} role="img" aria-label={ariaLabel}>
      {icon}
    </span>
  );

  if (variant === 'folder') {
    return body;
  }

  return (
    <Tooltip label={tooltip} multiline maw={320} position="top" openDelay={400}>
      {body}
    </Tooltip>
  );
};

export default React.memo(DocumentAvailabilityIcon);
